import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

function Sidebar({ onToggle }) {
  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [rol, setRol] = useState('');
  const [permisos, setPermisos] = useState({});
  const location = useLocation();

  useEffect(() => {
    const userRol = localStorage.getItem('rol');
    const userPermisos = localStorage.getItem('permisos');
    
    if (userRol) setRol(userRol);
    
    if (userPermisos) {
      try {
        setPermisos(JSON.parse(userPermisos));
      } catch (error) {
        setPermisos({});
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      
      if (mobile) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (onToggle) {
      onToggle(isOpen);
    }
  }, [isOpen, onToggle]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleLinkClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  // ⭐ SUPERADMIN TIENE TODOS LOS PERMISOS
  const tienePermiso = (permiso) => {
    if (rol === 'superadmin') return true;
    return permisos[permiso] === true;
  };

  const puedeVerDocumentos = () => {
    if (rol === 'superadmin') return true;
    return tienePermiso('verDocumentosMedicos') || tienePermiso('verDocumentosAdministrativos');
  };

  const puedeEnviarDocumentos = () => {
    if (rol === 'superadmin') return true;
    return tienePermiso('enviarDocumentosMedicos') || tienePermiso('enviarDocumentosAdministrativos');
  };

  // ════════════════════════════════════════════════════════════════════
  // ESTILOS RESPONSIVE
  // ════════════════════════════════════════════════════════════════════
  
  const sidebarWidth = isOpen ? '250px' : '70px';
  
  const sidebarStyle = {
    width: isMobile ? '250px' : sidebarWidth,
    backgroundColor: '#1a1e4b',
    minHeight: '100vh',
    height: '100%',
    color: 'white',
    transition: 'all 0.3s ease-in-out',
    position: 'fixed',
    top: 0,
    left: isMobile ? (isOpen ? '0' : '-260px') : '0',
    paddingTop: '15px',
    zIndex: 1050,
    boxShadow: isOpen ? '2px 0 15px rgba(0,0,0,0.2)' : 'none',
    overflowX: 'hidden',
    overflowY: 'auto'
  };

  const overlayStyle = {
    display: isMobile && isOpen ? 'block' : 'none',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1040,
    transition: 'opacity 0.3s ease'
  };

  const mobileToggleStyle = {
    display: isMobile && !isOpen ? 'flex' : 'none',
    position: 'fixed',
    top: '15px',
    left: '15px',
    zIndex: 1060,
    backgroundColor: '#1a1e4b',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    width: '45px',
    height: '45px',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
  };

  const linkStyle = {
    textDecoration: 'none',
    color: 'white',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '500',
    fontSize: '14px',
    backgroundColor: 'transparent',
    transition: 'all 0.3s ease',
    borderRadius: '0 25px 25px 0',
    margin: '2px 0',
    marginRight: '10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    minHeight: '48px'
  };

  const activeStyle = {
    backgroundColor: '#0f123a',
    borderLeft: '4px solid #ffffff'
  };

  const toggleButtonStyle = {
    color: 'white',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background-color 0.3s',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  // ⭐ MENÚ CON PERMISOS CORREGIDOS PARA SUPERADMIN
  const menuItems = [
    { 
      path: '/dashboard', 
      icon: '📊', 
      label: 'Dashboard', 
      mostrar: puedeVerDocumentos() 
    },
    { 
      path: '/medical-documents', 
      icon: '🏥', 
      label: 'Documentos Médicos', 
      mostrar: tienePermiso('verDocumentosMedicos') 
    },
    { 
      path: '/administrative-documents', 
      icon: '📋', 
      label: 'Docs Administrativos', 
      mostrar: tienePermiso('verDocumentosAdministrativos') 
    },
    { 
      path: '/send-document', 
      icon: '📤', 
      label: 'Enviar Documento', 
      mostrar: puedeEnviarDocumentos() 
    },
    { 
      path: '/plantillas', 
      icon: '📝', 
      label: 'Plantillas', 
      mostrar: rol === 'superadmin' 
    },
    { 
      path: '/reports', 
      icon: '📈', 
      label: 'Reportes', 
      mostrar: tienePermiso('verReportes') 
    },
    { 
      path: '/register', 
      icon: '➕', 
      label: 'Registrar Usuario', 
      mostrar: rol === 'superadmin' 
    },
    { 
      path: '/user-management', 
      icon: '👥', 
      label: 'Administración de Usuarios', 
      mostrar: rol === 'superadmin' 
    }
  ];

  const menuItemsVisibles = menuItems.filter(item => item.mostrar);
  const showLabels = isMobile ? true : isOpen;

  return (
    <>
      {/* Botón hamburguesa flotante para móviles */}
      <button
        onClick={toggleSidebar}
        style={mobileToggleStyle}
        aria-label="Abrir menú"
      >
        ☰
      </button>

      {/* Overlay para cerrar en móviles */}
      <div 
        style={overlayStyle} 
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar */}
      <div style={sidebarStyle}>
        {/* Header con logos */}
        <div className="d-flex align-items-center justify-content-center mb-3 px-2">
          {showLabels ? (
            <div className="d-flex align-items-center">
              <img 
                src="/logo1.png" 
                alt="Logo" 
                style={{ width: '50px', height: '50px' }} 
              />
              <img 
                src="/logo.png" 
                alt="Logo Empresa" 
                style={{ width: '100px', height: 'auto', marginLeft: '8px' }} 
              />
            </div>
          ) : (
            <img 
              src="/logo1.png" 
              alt="Logo" 
              style={{ width: '40px', height: '40px' }} 
            />
          )}
        </div>

        {/* Botón de toggle (hamburguesa) */}
        <div className="d-flex justify-content-end pe-3 mb-3">
          <button
            onClick={toggleSidebar}
            style={toggleButtonStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ☰
          </button>
        </div>

        {/* Menú de navegación */}
        <nav className="mt-2">
          <ul className="list-unstyled m-0 p-0">
            {menuItemsVisibles.map((item) => (
              <li key={item.path} className="mb-1">
                <Link
                  to={item.path}
                  style={{
                    ...linkStyle,
                    ...(location.pathname === item.path ? activeStyle : {}),
                    justifyContent: showLabels ? 'flex-start' : 'center',
                    padding: showLabels ? '12px 20px' : '12px 0'
                  }}
                  onClick={handleLinkClick}
                  title={!showLabels ? item.label : ''}
                  onMouseEnter={(e) => {
                    if (location.pathname !== item.path) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== item.path) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ 
                    fontSize: '18px', 
                    minWidth: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </span>
                  {showLabels && (
                    <span className="ms-3" style={{ fontSize: '14px' }}>
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            ))}

            {menuItemsVisibles.length === 0 && showLabels && (
              <li className="mb-1">
                <div style={{ ...linkStyle, opacity: 0.6, cursor: 'default', fontSize: '12px', textAlign: 'center' }}>
                  Sin permisos asignados
                </div>
              </li>
            )}
            
            {/* Cerrar sesión */}
            <li className="mb-1 mt-4">
              <Link
                to="#"
                style={{
                  ...linkStyle,
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  padding: showLabels ? '12px 20px' : '12px 0',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  marginTop: '20px'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.clear();
                  window.location.href = '/login';
                }}
                title={!showLabels ? 'Cerrar sesión' : ''}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '18px', minWidth: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  🚪
                </span>
                {showLabels && (
                  <span className="ms-3" style={{ fontSize: '14px' }}>
                    Cerrar sesión
                  </span>
                )}
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}

export default Sidebar;